import React, { useState, useEffect } from "react";
import { useLanguage } from '../contexts/LanguageContext';
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminSidebar from "../components/AdminSidebar";

// Add CSS styles for the form elements
const formInputStyle = {
  display: "block",
  width: "100%",
  padding: "8px",
  margin: "10px 0",
  borderRadius: "4px",
  border: "1px solid #ccc"
};

const formButtonStyle = {
  backgroundColor: "#4CAF50",
  color: "white",
  padding: "10px 15px",
  margin: "10px 0",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "16px"
};

const actionButtonStyle = {
  margin: "0 5px",
  padding: "6px 10px",
  borderRadius: "4px",
  border: "none",
  cursor: "pointer",
  fontSize: "14px"
};

const Orders = () => {
  const { t, language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbStatus, setDbStatus] = useState("جاري التحقق...");
  const [newOrder, setNewOrder] = useState({
    restaurantId: "",
    customerName: "",
    customerPhone: "",
    customerAddress: "",
    orderAmount: 0,
    paymentMethod: "cash",
    status: "pending"
  });
  const [activeTab, setActiveTab] = useState('list');
  const [updatedData, setUpdatedData] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);

  // Check Firebase connection
  useEffect(() => {
    const checkFirebaseConnection = async () => {
      try {
        console.log("📡 التحقق من اتصال Firebase...");
        // Try to access Firestore
        const testRef = collection(db, "_connection_test_");
        await getDocs(testRef);
        setDbStatus("✅ متصل بقاعدة البيانات");
        console.log("✅ الاتصال بـ Firebase ناجح");
      } catch (error) {
        console.error("❌ فشل الاتصال بـ Firebase:", error);
        setDbStatus(`❌ فشل الاتصال: ${error.message}`);
        setError(`مشكلة في الاتصال بقاعدة البيانات: ${error.message}`);
      }
    };

    checkFirebaseConnection();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        console.log("📌 جاري جلب بيانات الطلبات...");
        const querySnapshot = await getDocs(collection(db, "orders"));

        if (querySnapshot.empty) {
          console.warn("⚠️ لا توجد طلبات متاحة في Firestore.");
          setOrders([]);
        } else {
          const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("📌 الطلبات المسترجعة:", data);
          setOrders(data);
        }
      } catch (error) {
        console.error("❌ خطأ في جلب بيانات الطلبات:", error.message);
        setError(`خطأ في جلب الطلبات: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (dbStatus.includes("متصل")) {
      fetchOrders();
    }
  }, [dbStatus]);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        console.log("�� جاري جلب بيانات المطاعم...");
        const querySnapshot = await getDocs(collection(db, "restaurants"));

        if (querySnapshot.empty) {
          console.warn("⚠️ لا توجد مطاعم متاحة في Firestore.");
          setRestaurants([]);
        } else {
          const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("📌 المطاعم المسترجعة:", data);
          setRestaurants(data);
        }
      } catch (error) {
        console.error("❌ خطأ في جلب بيانات المطاعم:", error.message);
        setError(`خطأ في جلب المطاعم: ${error.message}`);
      }
    };

    if (dbStatus.includes("متصل")) {
      fetchRestaurants();
    }
  }, [dbStatus]);

  const addOrder = async () => {
    if (!newOrder.restaurantId || !newOrder.customerName || !newOrder.customerPhone || !newOrder.customerAddress) {
      setError(t('fill_all_fields'));
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "orders"), newOrder);
      setOrders([...orders, { id: docRef.id, ...newOrder }]);
      setNewOrder({
        restaurantId: "",
        customerName: "",
        customerPhone: "",
        customerAddress: "",
        orderAmount: 0,
        paymentMethod: "cash",
        status: "pending"
      });
    } catch (error) {
      setError(error.message);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: newStatus });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
    } catch (error) {
      setError(error.message);
    }
  };

  const startEditing = (order) => {
    setEditingOrder(order.id);
    setUpdatedData({
      restaurantId: order.restaurantId,
      customerName: order.customerName,
      status: order.status,
      paymentMethod: order.paymentMethod,
      total: order.orderAmount
    });
  };

  const saveChanges = async () => {
    if (!updatedData.restaurantId || !updatedData.customerName || !updatedData.status || !updatedData.paymentMethod || !updatedData.total) {
      setError(t('fill_all_fields'));
      return;
    }

    try {
      await updateDoc(doc(db, "orders", editingOrder), updatedData);
      setOrders((prev) =>
        prev.map((o) => (o.id === editingOrder ? { ...o, ...updatedData } : o))
      );
      setEditingOrder(null);
      setUpdatedData({});
    } catch (error) {
      setError(error.message);
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: "cancelled" });
      setOrders((prev) =>
        prev.filter((o) => o.id !== orderId)
      );
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return (
      <div className="orders-layout">
        <AdminSidebar />
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
        
        <div className="orders-content">
          <div className="orders-header">
            <h1>{t('orders_title')}</h1>
            <div className="tab-selector">
              <button 
                className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
                onClick={() => setActiveTab('list')}
              >
                {t('orders_list')}
              </button>
              <button 
                className={`tab-button ${activeTab === 'add' ? 'active' : ''}`}
                onClick={() => setActiveTab('add')}
              >
                {t('orders_add')}
              </button>
            </div>
          </div>

          <div className="dashboard-container">
            {activeTab === 'list' ? (
              <div className="orders-list-section">
                <div className="card">
                  <div className="card-header">
                    <h2>{t('orders_stats')}</h2>
                    <div className="order-stats">
                      <div className="stat">
                        <span className="stat-value">{orders.length}</span>
                        <span className="stat-label">{t('orders_total')}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{orders.filter(o => o.status === t('status_completed')).length}</span>
                        <span className="stat-label">{t('orders_completed')}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">
                          {orders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
                        </span>
                        <span className="stat-label">{t('orders_total_amount')}</span>
                      </div>
                    </div>
                  </div>

                  {orders.length === 0 ? (
                    <div className="no-data">
                      <div className="no-data-icon">📦</div>
                      <p>{t('orders_no_orders')}</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="orders-table">
                        <thead>
                          <tr>
                            <th>{t('orders_id')}</th>
                            <th>{t('orders_restaurant')}</th>
                            <th>{t('orders_customer')}</th>
                            <th>{t('orders_status')}</th>
                            <th>{t('orders_payment_method')}</th>
                            <th>{t('orders_total')}</th>
                            <th>{t('orders_actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <React.Fragment key={order.id}>
                              <tr className={editingOrder === order.id ? 'selected-row' : ''}>
                                <td>{order.id}</td>
                                <td>{order.restaurantName}</td>
                                <td>{order.customerName}</td>
                                <td>
                                  <span className={`status-badge ${order.status === t('status_completed') ? 'completed' : 'pending'}`}>
                                    {order.status}
                                  </span>
                                </td>
                                <td>{order.paymentMethod}</td>
                                <td>
                                  <div className="total-display">
                                    <span className="total-value">{order.total}</span>
                                    <span className="total-currency">{t('currency')}</span>
                                  </div>
                                </td>
                                <td className="actions-cell">
                                  <button 
                                    className="action-button edit"
                                    onClick={() => startEditing(order)}
                                  >
                                    {t('orders_edit')}
                                  </button>
                                  {editingOrder === order.id && (
                                    <button 
                                      className="action-button save"
                                      onClick={saveChanges}
                                    >
                                      {t('orders_save')}
                                    </button>
                                  )}
                                  <button 
                                    className="action-button delete"
                                    onClick={() => deleteOrder(order.id)}
                                  >
                                    {t('orders_delete')}
                                  </button>
                                </td>
                              </tr>
                              {editingOrder === order.id && (
                                <tr className="editing-row">
                                  <td colSpan="7">
                                    <div className="editing-form">
                                      <div className="form-row">
                                        <label>{t('orders_restaurant')}:</label>
                                        <select 
                                          value={updatedData.restaurantId || ''} 
                                          onChange={(e) => setUpdatedData({...updatedData, restaurantId: e.target.value})}
                                        >
                                          <option value="">{t('select_restaurant')}</option>
                                          {restaurants.map((restaurant) => (
                                            <option key={restaurant.id} value={restaurant.id}>
                                              {restaurant.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="form-row">
                                        <label>{t('orders_customer')}:</label>
                                        <input 
                                          type="text" 
                                          value={updatedData.customerName || ''} 
                                          onChange={(e) => setUpdatedData({...updatedData, customerName: e.target.value})}
                                        />
                                      </div>
                                      <div className="form-row">
                                        <label>{t('orders_status')}:</label>
                                        <select 
                                          value={updatedData.status || ''} 
                                          onChange={(e) => setUpdatedData({...updatedData, status: e.target.value})}
                                        >
                                          <option value={t('status_pending')}>{t('status_pending')}</option>
                                          <option value={t('status_completed')}>{t('status_completed')}</option>
                                        </select>
                                      </div>
                                      <div className="form-row">
                                        <label>{t('orders_payment_method')}:</label>
                                        <select 
                                          value={updatedData.paymentMethod || ''} 
                                          onChange={(e) => setUpdatedData({...updatedData, paymentMethod: e.target.value})}
                                        >
                                          <option value={t('payment_cash')}>{t('payment_cash')}</option>
                                          <option value={t('payment_card')}>{t('payment_card')}</option>
                                        </select>
                                      </div>
                                      <div className="form-row">
                                        <label>{t('orders_total')}:</label>
                                        <input 
                                          type="number" 
                                          value={updatedData.total || ''} 
                                          onChange={(e) => setUpdatedData({...updatedData, total: Number(e.target.value)})}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="add-order-section">
                <div className="card">
                  <div className="card-header">
                    <h2>{t('orders_add_new')}</h2>
                  </div>
                  <div className="card-body">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>{t('orders_restaurant')}</label>
                        <select 
                          value={newOrder.restaurantId} 
                          onChange={(e) => setNewOrder({ ...newOrder, restaurantId: e.target.value })}
                          className="form-control"
                        >
                          <option value="">{t('select_restaurant')}</option>
                          {restaurants.map((restaurant) => (
                            <option key={restaurant.id} value={restaurant.id}>
                              {restaurant.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('orders_customer')}</label>
                        <input 
                          type="text" 
                          placeholder={t('orders_customer')} 
                          value={newOrder.customerName} 
                          onChange={(e) => setNewOrder({ ...newOrder, customerName: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('orders_status')}</label>
                        <select 
                          value={newOrder.status} 
                          onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                          className="form-control"
                        >
                          <option value={t('status_pending')}>{t('status_pending')}</option>
                          <option value={t('status_completed')}>{t('status_completed')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('orders_payment_method')}</label>
                        <select 
                          value={newOrder.paymentMethod} 
                          onChange={(e) => setNewOrder({ ...newOrder, paymentMethod: e.target.value })}
                          className="form-control"
                        >
                          <option value={t('payment_cash')}>{t('payment_cash')}</option>
                          <option value={t('payment_card')}>{t('payment_card')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('orders_total')}</label>
                        <input 
                          type="number" 
                          placeholder={t('orders_total')} 
                          value={newOrder.total} 
                          onChange={(e) => setNewOrder({ ...newOrder, total: Number(e.target.value) })}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button 
                        className="add-button"
                        onClick={addOrder} 
                        disabled={loading}
                      >
                        {t('orders_add')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-layout">
      <AdminSidebar />
      <div className="orders-content">
        <div className="orders-header">
          <h1>{t('orders_title')}</h1>
          <div className="tab-selector">
            <button 
              className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
              onClick={() => setActiveTab('list')}
            >
              {t('orders_list')}
            </button>
            <button 
              className={`tab-button ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => setActiveTab('add')}
            >
              {t('orders_add')}
            </button>
          </div>
        </div>

        <div className="dashboard-container">
          {activeTab === 'list' ? (
            <div className="orders-list-section">
              <div className="card">
                <div className="card-header">
                  <h2>{t('orders_stats')}</h2>
                  <div className="order-stats">
                    <div className="stat">
                      <span className="stat-value">{orders.length}</span>
                      <span className="stat-label">{t('orders_total')}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{orders.filter(o => o.status === t('status_completed')).length}</span>
                      <span className="stat-label">{t('orders_completed')}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {orders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
                      </span>
                      <span className="stat-label">{t('orders_total_amount')}</span>
                    </div>
                  </div>
                </div>

                {orders.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon">📦</div>
                    <p>{t('orders_no_orders')}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>{t('orders_id')}</th>
                          <th>{t('orders_restaurant')}</th>
                          <th>{t('orders_customer')}</th>
                          <th>{t('orders_status')}</th>
                          <th>{t('orders_payment_method')}</th>
                          <th>{t('orders_total')}</th>
                          <th>{t('orders_actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <React.Fragment key={order.id}>
                            <tr className={editingOrder === order.id ? 'selected-row' : ''}>
                              <td>{order.id}</td>
                              <td>{order.restaurantName}</td>
                              <td>{order.customerName}</td>
                              <td>
                                <span className={`status-badge ${order.status === t('status_completed') ? 'completed' : 'pending'}`}>
                                  {order.status}
                                </span>
                              </td>
                              <td>{order.paymentMethod}</td>
                              <td>
                                <div className="total-display">
                                  <span className="total-value">{order.total}</span>
                                  <span className="total-currency">{t('currency')}</span>
                                </div>
                              </td>
                              <td className="actions-cell">
                                <button 
                                  className="action-button edit"
                                  onClick={() => startEditing(order)}
                                >
                                  {t('orders_edit')}
                                </button>
                                {editingOrder === order.id && (
                                  <button 
                                    className="action-button save"
                                    onClick={saveChanges}
                                  >
                                    {t('orders_save')}
                                  </button>
                                )}
                                <button 
                                  className="action-button delete"
                                  onClick={() => deleteOrder(order.id)}
                                >
                                  {t('orders_delete')}
                                </button>
                              </td>
                            </tr>
                            {editingOrder === order.id && (
                              <tr className="editing-row">
                                <td colSpan="7">
                                  <div className="editing-form">
                                    <div className="form-row">
                                      <label>{t('orders_restaurant')}:</label>
                                      <select 
                                        value={updatedData.restaurantId || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, restaurantId: e.target.value})}
                                      >
                                        <option value="">{t('select_restaurant')}</option>
                                        {restaurants.map((restaurant) => (
                                          <option key={restaurant.id} value={restaurant.id}>
                                            {restaurant.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="form-row">
                                      <label>{t('orders_customer')}:</label>
                                      <input 
                                        type="text" 
                                        value={updatedData.customerName || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, customerName: e.target.value})}
                                      />
                                    </div>
                                    <div className="form-row">
                                      <label>{t('orders_status')}:</label>
                                      <select 
                                        value={updatedData.status || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, status: e.target.value})}
                                      >
                                        <option value={t('status_pending')}>{t('status_pending')}</option>
                                        <option value={t('status_completed')}>{t('status_completed')}</option>
                                      </select>
                                    </div>
                                    <div className="form-row">
                                      <label>{t('orders_payment_method')}:</label>
                                      <select 
                                        value={updatedData.paymentMethod || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, paymentMethod: e.target.value})}
                                      >
                                        <option value={t('payment_cash')}>{t('payment_cash')}</option>
                                        <option value={t('payment_card')}>{t('payment_card')}</option>
                                      </select>
                                    </div>
                                    <div className="form-row">
                                      <label>{t('orders_total')}:</label>
                                      <input 
                                        type="number" 
                                        value={updatedData.total || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, total: Number(e.target.value)})}
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="add-order-section">
              <div className="card">
                <div className="card-header">
                  <h2>{t('orders_add_new')}</h2>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('orders_restaurant')}</label>
                      <select 
                        value={newOrder.restaurantId} 
                        onChange={(e) => setNewOrder({ ...newOrder, restaurantId: e.target.value })}
                        className="form-control"
                      >
                        <option value="">{t('select_restaurant')}</option>
                        {restaurants.map((restaurant) => (
                          <option key={restaurant.id} value={restaurant.id}>
                            {restaurant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('orders_customer')}</label>
                      <input 
                        type="text" 
                        placeholder={t('orders_customer')} 
                        value={newOrder.customerName} 
                        onChange={(e) => setNewOrder({ ...newOrder, customerName: e.target.value })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('orders_status')}</label>
                      <select 
                        value={newOrder.status} 
                        onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                        className="form-control"
                      >
                        <option value={t('status_pending')}>{t('status_pending')}</option>
                        <option value={t('status_completed')}>{t('status_completed')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('orders_payment_method')}</label>
                      <select 
                        value={newOrder.paymentMethod} 
                        onChange={(e) => setNewOrder({ ...newOrder, paymentMethod: e.target.value })}
                        className="form-control"
                      >
                        <option value={t('payment_cash')}>{t('payment_cash')}</option>
                        <option value={t('payment_card')}>{t('payment_card')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('orders_total')}</label>
                      <input 
                        type="number" 
                        placeholder={t('orders_total')} 
                        value={newOrder.total} 
                        onChange={(e) => setNewOrder({ ...newOrder, total: Number(e.target.value) })}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button 
                      className="add-button"
                      onClick={addOrder} 
                      disabled={loading}
                    >
                      {t('orders_add')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
